<template>
    <div>
        <header>
            <div class="flex flex-wrap items-center justify-start md:justify-between py-4 gap-x-2">
                <div class="sm:flex-auto">
                    <h1 class="text-xl font-semibold text-gray-900 select-none"> {{ searchParams.archived_filter ? "Archived " : "" }} Maintenance Requests</h1>
                </div>

                <div class="flex flex-wrap items-center gap-2">
                    <a :href="publicPageUrl" target="_blank"
                       class="font-bold whitespace-nowrap cursor-pointer" title="Public Maintenance Request Link">
                        <badge class="bg-indigo-100 text-indigo-600 hover:text-indigo-700 transition duration-300" text-size="text-md"> {{ publicPageUrl }}</badge>
                    </a>

                    <button @click.prevent="copyPublicPageUrl()">
                        <icon-copy class="w-4 h-4 text-gray-800 hover:text-indigo-600 transition duration-300"></icon-copy>
                    </button>

                    <secondary-button @click.prevent="toggleList()" class="whitespace-nowrap">
                        Share Link
                    </secondary-button>

                    <simple-dropdown label="Options">
                        <template slot="buttonOptions">
                            <simple-dropdown-option
                                value="Show Archived Maintenance Requests"
                                key="archived"
                                @click="toggleShowArchivedRequests">
                                {{ searchParams.archived_filter ? "View Current" : "View Archived" }}
                            </simple-dropdown-option>
                            <simple-dropdown-option
                                value="Settings"
                                key="ticket_settings"
                                v-if="userCan('manage-ticket-settings')"
                                @click="openLinkInNewTab('/settings/tickets#tab-maintenance-requests')">
                                Settings
                            </simple-dropdown-option>
                        </template>
                    </simple-dropdown>
                </div>
            </div>
        </header>

        <div class="grid grid-cols-3 gap-4">
            <div class="flex relative" :class="selectedRequest ? 'col-span-2 hidden md:block' : 'col-span-3'">
                <div class="flex-grow sm:px-0">
                    <!-- Filters -->
                    <expandable-filters-container
                        :showExpandOption="false"
                        :stickHeaderOnScroll="notSmallScreen"
                        :advancedFiltersExpanded="false">
                        <div slot="top-panel-left">
                            <div class="flex flex-wrap pb-3 pt-4 sm:py-0 sm:justify-start sm:items-center gap-4">
                                <div class="relative z-0 inline-flex shadow-sm rounded-md">
                                    <button v-for="status in statuses"
                                            v-if="(!searchParams.archived_filter && status === 'new') || status !== 'new'"
                                            class="relative inline-flex items-center px-4 py-2 border border-indigo-300 first:rounded-l-md last:rounded-r-md bg-white text-sm font-medium transition duration-300 hover:bg-indigo-50 hover:text-indigo-500"
                                            :class="isStatusFilterActive(status) ? 'bg-indigo-200 text-indigo-600' : 'bg-white'"
                                            @click="toggleStatusFilter(status)">
                                        {{ status | titlecase }}
                                    </button>
                                </div>

                                <!-- Search Input Filter -->
                                <simple-text-search-input
                                    text-search-placeholder="eg. 100 Example Road"
                                    text-search-label="Search Assets or Contacts"
                                    v-model="searchParams.filter_text"
                                    @input="fetchMaintenanceRequests">
                                </simple-text-search-input>

                                <!-- Date Picker Filter -->
                                <div class="flex mt-1">
                                    <el-date-picker
                                        :style="notSmallScreen ? 'min-width: 250px' : 'max-width: 19.5rem'"
                                        v-model="searchParams.created_at_date_range"
                                        value-format="yyyy-MM-dd"
                                        type="daterange"
                                        range-separator="-"
                                        :start-placeholder="notSmallScreen ? 'From date' : 'From'"
                                        :end-placeholder="notSmallScreen ? 'To date' : 'To'"
                                        @change="fetchMaintenanceRequests">
                                    </el-date-picker>
                                </div>

                                <toggle @input="toggleWithoutTicketsFilter" v-model="searchParams.without_tickets"
                                        active-color-bg-class="bg-indigo-600"
                                        :title="searchParams.without_tickets ? `Show requests with or without tickets` : `Only show requests without tickets`">
                                    Without Tickets
                                </toggle>
                            </div>
                        </div>
                    </expandable-filters-container>

                    <!-- List -->
                    <div class="mt-4 overflow-hidden shadow sm:mx-0 rounded-lg">
                        <template v-if="loading"
                                  class="flex justify-center items-center w-full p-20">
                            <div class="flex flex-col items-center">
                                <h2 class="text-xl font-normal text-gray-400 py-36 select-none flex align-items-center">
                                    <icon-refresh class="h-8 w-8 text-indigo-600 animate-spin mr-2"/>
                                    Loading Maintenance Requests...
                                </h2>
                            </div>
                        </template>

                        <!-- Empty View -->
                        <template v-else-if="!loading && maintenanceRequests.length === 0">
                            <div class="flex justify-around items-center text-center" style="min-height: 400px;">
                                <div class="flex flex-col items-center">
                                    <h2 class="block text-lg font-medium text-gray-400 mb-4">
                                        No {{ searchParams.status | titlecase }} Maintenance Requests
                                    </h2>
                                    <!-- Maintenance Request Icon / SVG -->
                                    <div class="block text-center pb-8">
                                        <svg class="text-center text-gray-400 fill-current" height="64" viewBox="0 0 60 61" width="64"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <g id="008---IT-Request">
                                                <path id="Shape"
                                                      d="m46.279 11.6c-.1293785-.307415-.4035058-.5300912-.7309099-.5937253-.3274041-.063634-.6649824.0401516-.9000901.2767253l-5.029 5.055h-2.89v-2.922l5.032-5.068c.2340244-.23588617.3355959-.57260952.2710474-.8985592s-.2867852-.59854864-.5930474-.7274408c-3.3206308-1.40150388-7.1548384-.72065924-9.7899426 1.73841135-2.6351041 2.45907055-3.5789974 6.23713395-2.4100574 9.64658865l-.591.6-4.419-4.447.016-.016c.2019865-.2027357.3074336-.4822958.2896535-.7679252-.0177802-.2856294-.1570836-.549954-.3826535-.7260748l-6.464-5.05c-.3987355-.31204784-.967783-.27683132-1.325.082l-2.873 2.885c-.3541968.3561119-.3892874.9197023-.082 1.317l5.028 6.5c.1773179.2291636.4453174.3701495.7346126.3864559.2892952.0163065.5714404-.0936699.7733874-.3014559l4.415 4.438-10.172 10.223c-1.5739911 1.589625-1.5739911 4.150375 0 5.74.7527402.7712119 1.784824 1.2060612 2.8625 1.2060612s2.1097598-.4348493 2.8625-1.2060612l8.73-8.769 9.272 9.31c.9463514.9541974 2.2345976 1.4909244 3.5785 1.4909244s2.6321486-.536727 3.5785-1.4909244c1.9687905-1.987665 1.9687905-5.190335 0-7.178l-8.012-8.055c3.1981832.2831219 6.3104312-1.1324154 8.1988198-3.7290522 1.8883886-2.5966367 2.2760256-5.9936311 1.0211802-8.9489478zm-25.625 4.85c-.3612344-.3534253-.9270579-.3883369-1.329-.082l-3.8-4.908 1.628-1.636 4.882 3.816c-.0316146.0485871-.0590465.099771-.082.153-.0750219.1096563-.1157473.2391421-.117.372-.065.649-.062.469 5.391 5.96l-1.46 1.467zm-2.16 21.114c-.8057835.7977753-2.1057247.7912834-2.9035-.0145-.7977753-.8057835-.7912834-2.1057247.0145-2.9035 7.348-7.39-7.017 7.054 13.67-13.746.013-.01.019-.027.031-.038.228-.2-.548.574 1.8-1.8.2849358-.2877086.3682428-.7188521.211-1.092-1.0278197-2.4197406-.6468883-5.2081417.9922922-7.2635359 1.6391805-2.05539418 4.2729398-3.04716104 6.8607078-2.5834641l-4.151 4.177c-.1859605.1874915-.2902107.4409275-.29.705v4.329c0 .5522847.4477153 1 1 1h4.306c.2661757-.0000552.5213461-.1062262.709-.295l4.142-4.163c.4810869 2.5811263-.4936671 5.2179958-2.5379546 6.8655837s-4.8280493 2.039884-7.2480454 1.0214163c-.3761502-.1598575-.8117223-.0747229-1.1.215l-1.757 1.767c-2.136 2.145-4.419 4.43-4.314 4.325-.964.968 3.499-3.514-9.435 9.489zm25.159.541c-1.2068551 1.1598044-3.1141449 1.1598044-4.321 0l-9.278-9.318 4.334-4.354 9.265 9.314c1.190063 1.2069873 1.190063 3.1460127 0 4.353z"/>
                                                <path id="Shape"
                                                      d="m53 0h-46c-3.86416566.00440864-6.99559136 3.13583434-7 7v34c.00440864 3.8641657 3.13583434 6.9955914 7 7h10.038c0 1.781-.391 5.856-3.592 8.479-.6810212.5440611-.9312471 1.4665293-.6185076 2.2801548.3127394.8136255 1.1164086 1.331 1.9865076 1.2788452 4.949-.338 13.456-2.389 15.953-12.038h22.233c3.8641657-.0044086 6.9955914-3.1358343 7-7v-34c-.0044086-3.86416566-3.1358343-6.99559136-7-7zm5 41c-.0033061 2.7600532-2.2399468 4.9966939-5 5-24.3 0-23.006-.032-23.3.06-.2413343.0740365-.4438977.239966-.564.462-.04.071-.117.269-.117.269-1.94 9.067-9.819 10.942-14.305 11.234 5-4.1 4.311-10.85 4.28-11.135 0-.011-.082-.282-.1-.324-.0628565-.1085564-.1383857-.2092619-.225-.3-.399-.366.145-.266-11.669-.266-2.76005315-.0033061-4.99669388-2.2399468-5-5v-34c.00330612-2.76005315 2.23994685-4.99669388 5-5h46c2.7600532.00330612 4.9966939 2.23994685 5 5z"/>
                                                <path id="Shape"
                                                      d="m7 4h-1c-.55228475 0-1 .44771525-1 1s.44771525 1 1 1h1c.55228475 0 1-.44771525 1-1s-.44771525-1-1-1z"/>
                                                <path id="Shape"
                                                      d="m17 5c0-.55228475-.4477153-1-1-1h-5c-.5522847 0-1 .44771525-1 1s.4477153 1 1 1h5c.5522847 0 1-.44771525 1-1z"/>
                                                <path id="Shape"
                                                      d="m45 42h-1c-.5522847 0-1 .4477153-1 1s.4477153 1 1 1h1c.5522847 0 1-.4477153 1-1s-.4477153-1-1-1z"/>
                                                <path id="Shape"
                                                      d="m54 42h-5c-.5522847 0-1 .4477153-1 1s.4477153 1 1 1h5c.5522847 0 1-.4477153 1-1s-.4477153-1-1-1z"/>
                                            </g>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <ul v-else-if="!loading && maintenanceRequests.length > 0">
                            <li v-for="(request, index) in maintenanceRequests"
                                class="border-gray-200 transition duration-300 rounded-sm bg-white"
                                :class="{
                                'border-t': index !== 0,
                                'bg-indigo-50': selectedRequest && selectedRequest.id === request.id, 'even:bg-gray-100 hover:bg-gray-50':
                                !(selectedRequest && selectedRequest.id === request.id)
                            }">
                                <button class="focus:outline-none transition duration-300 w-full grid grid-cols-2 pl-4 md:pl-6 p-4 gap-4"
                                        @click.prevent="selectRequest(request)">
                                    <div class="flex flex-col justify-start w-full gap-2 col-span-2 sm:col-span-1">
                                        <div class="flex flex-wrap items-center leading-5 text-black font-medium gap-1 text-left">
                                            <div class="flex items-center justify-start gap-1">
                                                <div class="text-baseline font-bold"
                                                      :class="[request.status === 'closed' ? 'text-green-500' :'text-indigo-600']">
                                                    {{ request.reference_number }} &nbsp; &middot; &nbsp;
                                                    <span v-if="request.asset && request.asset.name" class="font-medium text-gray-900 mr-2">
                                                        {{ request.asset.name }}
                                                    </span>
                                                    <span v-else class="font-medium text-gray-900 mr-2">
                                                        {{ request.property_reference }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <span class="flex items-center w-full gap-2 font-medium">
                                            <span class="text-indigo-600 truncate">
                                                {{ request.contact.name }}
                                            </span>
                                        </span>

                                        <span class="flex flex-wrap items-start sm:items-center sm:flex-row leading-5 gap-2">
                                            <span class="text-sm text-gray-400">
                                                Reported
                                                <time class="font-medium text-gray-600" :datetime="request.created_at">
                                                    {{ request.created_at | datetime }}
                                                </time>
                                            </span>
                                        </span>
                                    </div>

                                    <span class="flex flex-col items-start">
                                        <span class="flex items-center justify-between w-full col-span-2 sm:col-span-1">
                                            <span class="flex flex-col items-start">
                                                <span v-if="request.issues.length !== 0" class="text-sm ml-0 leading-5 text-gray-500 font-normal text-left line-clamp-3">
                                                    {{ request.issues[0].location }} <span v-if="request.issues[0].location">- </span>
                                                    {{ request.issues[0].item }} {{ request.issues[0].description }}
                                                    <span v-if="request.issues.length > 1"
                                                          class="text-gray-400 rounded-md ml-2 py-0 px-1 font-normal">+
                                                        {{ request.issues.length - 1 }} more</span>
                                                </span>

                                                <span class="mt-3 flex flex-wrap items-center text-sm leading-5 text-gray-500 gap-2">
                                                    <template v-for="ticket in request.tickets.slice(0,3)">
                                                        <div class="flex items-center px-2 py-0.5 rounded-md text-xs font-medium"
                                                             :class="getTicketReferenceColorStyles(ticket.status,ticket.priority)"
                                                             @click.prevent="goToTicket(ticket.id)"
                                                        >
                                                            <icon-hammer-and-wrench class="h-4 w-4 mr-2"></icon-hammer-and-wrench>
                                                            {{ ticket.reference }}
                                                            <span class="inline-flex ml-1 items-center leading-5 mr-2">
                                                                {{ ticket.status | titlecase }}
                                                            </span>
                                                        </div>
                                                    </template>

                                                    <template v-if="request.tickets.length > 5">
                                                        <div class="flex items-center px-2 py-0.5 border border-gray-200 rounded">
                                                            <icon-hammer-and-wrench class="h-4 w-4 mr-2"></icon-hammer-and-wrench>
                                                            + {{ request.tickets.length - 5 }} {{ "Ticket" | pluralizeOnlyS(request.tickets.length - 5) }}
                                                        </div>
                                                    </template>
                                                </span>
                                            </span>

                                            <span>
                                                <icon-chevron-right class="h-5 w-5 text-gray-400" :class="notSmallScreen ? 'flex' : 'hidden'"></icon-chevron-right>
                                            </span>
                                        </span>
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </div>

                    <simple-pagination
                        v-if="maintenanceRequests && maintenanceRequests.length > 0"
                        :meta="paginationData"
                        padding="4"
                        per-page="15"
                        side-navigation-count="3"
                        @page-changed="fetchMaintenanceRequests">
                    </simple-pagination>
                </div>
            </div>

            <!-- Detail Panel -->
            <maintenance-requests-detail-panel v-if="selectedRequest" :class="notSmallScreen? 'col-span-1' : 'col-span-3'"
                                               :maintenance-request="selectedRequest"
                                               @update-request="setRequest"
                                               @close="selectRequest"
                                               @fetch-requests="fetchMaintenanceRequests()">
            </maintenance-requests-detail-panel>
        </div>
    </div>
</template>

<script>
import ComposeMail from "../../mixins/compose-mail";
import CopyToClipboard from "../../mixins/copy-to-clipboard";
import ticketSearch from "../../mixins/ticket-search";
import MaintenanceRequestsDetailPanel from "./MaintenanceRequestsDetailPanel";
import TicketOutline from "../library/icons/TicketOutline.vue";
import maintenanceRequestMixin from "../../mixins/maintenance-request-mixin";

export default {
    name: "MaintenanceRequestsList",

    mixins: [
        ComposeMail,
        CopyToClipboard,
        ticketSearch,
        maintenanceRequestMixin
    ],

    components: {TicketOutline, MaintenanceRequestsDetailPanel},

    props: {
        publicPageUrl: {
            required: true,
            description: "Used for displaying the link at the top of the page"
        },
    },

    data () {
        return {
            maintenanceRequests: [],
            paginationData: {},
            searchParams: {
                status: "new",
                filter_text: "",
                archived_filter: false,
                without_tickets: false
            },
            selectedRequest: null,
            loading: true,
            statuses: ["new", "closed"],
        };
    },

    watch: {
        searchParams: {
            handler (val) {
                this.$storage.set("maintenanceRequests.searchParams", val);
            },
            deep: true
        },
    },
    mounted () {
        this.searchParams = this.$storage.get("maintenanceRequests.searchParams", {
            status: "new",
            filter_text: "",
            archived_filter: false,
            without_tickets: false
        });
        this.$set(this.searchParams, "archived_filter", false);

        let url = new URL(window.location.href);

        if (url.searchParams.get("status")) {
            this.$set(this.searchParams, "status", url.searchParams.get("status"));
        }

        if (url.searchParams.get("without_tickets")) {
            this.$set(this.searchParams, "without_tickets", url.searchParams.get("without_tickets") === "true");
        }

        if (url.search.includes("created_at_date_range")) {
            this.$set(this.searchParams, "created_at_date_range", []);
            this.searchParams.created_at_date_range.push(url.searchParams.get("created_at_date_range[0]"));
            this.searchParams.created_at_date_range.push(url.searchParams.get("created_at_date_range[1]"));
        }

        if (url.searchParams.get("clear_date") === "true") {
            this.$set(this.searchParams, "created_at_date_range", null);
        }

        if (url.searchParams.get("filter_text")) {
            this.$set(this.searchParams, "filter_text", url.searchParams.get("filter_text"));
        }

        if (url.searchParams.get("clear_filter_text") === "true") {
            this.$set(this.searchParams, "filter_text", null);
        }

        this.fetchMaintenanceRequests();
    },

    methods: {
        fetchMaintenanceRequests: _.debounce(function (paginationSettings = null) {
            this.loading = true;
            this.$set(this.searchParams, "page", paginationSettings?.pageNumber ? paginationSettings.pageNumber : 1);
            this.$set(this.searchParams, "per_page", paginationSettings?.perPage ? paginationSettings.perPage : 15);

            axios.get("/maintenance/requests", {
                params: this.searchParams
            })
            .then(response => {
                this.maintenanceRequests = response.data.data;
                this.paginationData = response.data.meta;
                this.loading = false;
            })
            .catch(error => {
                this.loading = false;
                toastr.error("Could not fetch maintenance requests. Error: " + error.message);
            });

        }, 300),

        selectRequest (request) {
            // deselect row
            if (this.selectedRequest && this.selectedRequest.id === request.id) {
                this.selectedRequest = null;
                return;
            }
            this.selectedRequest = request;
        },

        setRequest (request, remove = false) {
            let index = this.maintenanceRequests.findIndex(mr => mr.id === request.id);
            if (index !== -1) {
                if (remove) {
                    this.maintenanceRequests.splice(index, 1);
                    this.selectedRequest = null;
                } else {
                    this.maintenanceRequests.splice(index, 1, request);
                }
            }
        },

        isStatusFilterActive (status) {
            return this.searchParams.status === status;
        },

        toggleStatusFilter (status) {
            this.$set(this.searchParams, "status", status);
            this.selectedRequest = null;
            this.fetchMaintenanceRequests();
        },

        toggleWithoutTicketsFilter () {
            this.selectedRequest = null;
            this.fetchMaintenanceRequests();
        },

        toggleShowArchivedRequests () {
            this.$set(this.searchParams, "archived_filter", !this.searchParams.archived_filter);
            this.searchParams.archived_filter ? this.$set(this.searchParams, "status", "closed") : null;
            this.selectedRequest = null;
            this.fetchMaintenanceRequests();
        },

        toggleList () {
            this.$emit("toggle-list");
        },

        openLinkInNewTab (url) {
            window.open(url, "_blank");
        },

        copyPublicPageUrl () {
            this.copyToClipboard(this.publicPageUrl);
            toastr.success("Link Copied!");
        },
    },
};
</script>
